import React, { createContext, useContext, useMemo, useReducer } from 'react'

export type CartItem = {
  id: string
  name: string
  price: number
  image: string
  qty: number
}

type CartState = {
  items: CartItem[]
}

type Action =
  | { type: 'add'; item: Omit<CartItem, 'qty'>; qty?: number }
  | { type: 'remove'; id: string }
  | { type: 'update'; id: string; qty: number }
  | { type: 'clear' }

const CartContext = createContext<{
  state: CartState
  add: (item: Omit<CartItem, 'qty'>, qty?: number) => void
  remove: (id: string) => void
  update: (id: string, qty: number) => void
  clear: () => void
  total: number
} | null>(null)

const reducer = (state: CartState, action: Action): CartState => {
  switch (action.type) {
    case 'add': {
      const qty = action.qty ?? 1
      const existing = state.items.find(i => i.id === action.item.id)
      if (existing) {
        return {
          items: state.items.map(i =>
            i.id === action.item.id ? { ...i, qty: i.qty + qty } : i
          ),
        }
      }
      return { items: [...state.items, { ...action.item, qty }] }
    }
    case 'remove':
      return { items: state.items.filter(i => i.id !== action.id) }
    case 'update':
      return {
        items: state.items.map(i => (i.id === action.id ? { ...i, qty: action.qty } : i)),
      }
    case 'clear':
      return { items: [] }
    default:
      return state
  }
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, { items: [] }, () => {
    try {
      const raw = window.localStorage.getItem('ca_cart')
      if (!raw) return { items: [] }
      const parsed = JSON.parse(raw)
      if (!parsed || !Array.isArray(parsed.items)) return { items: [] }
      return { items: parsed.items }
    } catch {
      return { items: [] }
    }
  })

  const total = useMemo(() => state.items.reduce((sum, i) => sum + i.price * i.qty, 0), [state.items])

  React.useEffect(() => {
    try {
      window.localStorage.setItem('ca_cart', JSON.stringify({ items: state.items }))
    } catch {
      // ignore storage errors
    }
  }, [state.items])

  const value = {
    state,
    add: (item: Omit<CartItem, 'qty'>, qty?: number) => dispatch({ type: 'add', item, qty }),
    remove: (id: string) => dispatch({ type: 'remove', id }),
    update: (id: string, qty: number) => dispatch({ type: 'update', id, qty }),
    clear: () => dispatch({ type: 'clear' }),
    total,
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
